import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AnimatedWrapper } from '@atlassian/ai-common';

import { onNonGenerativeAIAnswerResultShown } from '../../../../../ai-answer-dialog/on-non-generative-ai-answer-result-shown';
import { SearchAIAnalyticsContext } from '../../../../../ai-answer-dialog/search-ai-analytics-context';
import { SearchAIAnalyticsContextLocation } from '../../../../../ai-answer-dialog/search-ai-analytics-context-location';
import type { NonGenerativeAIAnswerResultShownProps } from '../../../../../ai-answer-dialog/types';
import { useSAINUsageCriteriaMet } from '../../../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../../../common/controller/analytics/use-analytics';
import { AiBrand, AnswerCardType } from '../../../../../constants';
import { onNonGenerativeKnowledgeCardLinkClick } from '../../../../../knowledge-cards/on-non-generative-knowledge-card-link-click';
import { sha256Hash } from '../../../../../sha256-hash';
import { ConversationStartersBanner } from '../../../../../v2/search-page/convo-starters/conversation-starters-banner';
import { AIDisclaimerAndFeedback } from '../../../../common/ui/ai-disclaimer-and-feedback';
import { SmartAnswerCardBodyGenerating } from '../../../../common/ui/smart-answer-card-body-generating';
import { useTwgSources } from '../../../api/useTwgSources/useTwgSources';
import type { NavigationalCardProps, SearchResultData } from '../types';

import { AdditionalInfoSection } from './additional-info-section/AdditionInfoSection';
import { deduplicateContributors } from './additional-info-section/utils';
import { ConfluenceFlexibleSmartCard } from './ConfluenceFlexibleSmartCard';
import { navigateToLink } from './utils/navigate-to-link';

const styles = cssMap({
	disclaimerSpacer: {
		height: '36px',
	},
	root: {
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		width: '100%',
	},
	smartCardContainer: {
		minHeight: '121px',
		paddingTop: token('space.150'),
		paddingBottom: token('space.200'),
	},
	additionalInfoSectionContainer: {
		minHeight: '103px',
		paddingTop: token('space.150'),
		paddingBottom: token('space.200'),
	},
	conversationStartersContainer: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
	},
	borderBottomSeparator: {
		borderBottomStyle: 'solid',
		borderBottomWidth: token('border.width'),
		borderBottomColor: token('color.border'),
	},
	sidePadding: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	feedbackContainer: {
		paddingTop: token('space.050'),
		paddingBottom: token('space.100'),
	},
});

export const ConfluenceResultCardLoading = (): React.JSX.Element => (
	<>
		<AnimatedWrapper>
			<SmartAnswerCardBodyGenerating heightOverride="navigationalCard" />
		</AnimatedWrapper>
		<Box xcss={styles.disclaimerSpacer}></Box>
	</>
);

const ConfluenceCardComponent = ({
	searchResultData,
	workspaceId,
	source,
	searchPageAnalyticsCallbacks,
	onNavigate,
}: NavigationalCardProps) => {
	const renderStartTime = useRef(performance.now());
	const hasFiredNavCardLoaded = useRef(false);
	const {
		query,
		url,
		cloudId,
		searchSessionId,
		id: contentId,
		type: contentType,
		description,
		areLLMPoweredFeaturesEnabled,
		primaryProduct,
		linkedResults,
		extractedLinks,
	} = searchResultData as SearchResultData;
	const { trackAIInteractionInit } = useAIEventsInstrumentation();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const {
		loading: twgSourcesLoading,
		contributors,
		outgoingLinks,
	} = useTwgSources({
		contentId,
		workspaceId: workspaceId || '',
		cloudId: cloudId || '',
		product: primaryProduct || '',
	});
	const deduplicatedContributors = useMemo(
		() => deduplicateContributors(contributors),
		[contributors],
	);

	/**
	 * The priority for how we show the related links in the UI:
	 * P1: BE(these are part of the search results) related links coming from search-page
	 * P2: FE(these are extracted from the description) related links coming from search-page
	 * P3: Outgoing links from the TWG API (pages mentioned in the confluence page)
	 */
	const links = useMemo(() => {
		return [
			...(linkedResults?.map((result) => result.url) || []),
			...(extractedLinks || []),
			...(outgoingLinks || []),
		].slice(0, 50); // Limit the number of links to 50 to avoid errors from Object Resolver
	}, [linkedResults, extractedLinks, outgoingLinks]);

	const analyticsAttributes = useMemo<NonGenerativeAIAnswerResultShownProps>(
		() => ({
			searchSessionId,
			queryHash: sha256Hash(query),
			brand: AiBrand.ROVO,
			source,
			query,
			answerString: url || '',
			answerCardType: AnswerCardType.NAV_CONTENT,
		}),
		[searchSessionId, query, url, source],
	);

	// removing UGC attributes
	const analyticsAttributesWithoutUGC = useMemo<
		Omit<NonGenerativeAIAnswerResultShownProps, 'query' | 'answerString'>
	>(
		() => ({
			searchSessionId,
			queryHash: sha256Hash(query),
			brand: AiBrand.ROVO,
			source,
			answerCardType: AnswerCardType.NAV_CONTENT,
		}),
		[query, searchSessionId, source],
	);

	const fireNavCardLoaded = useCallback(() => {
		if (hasFiredNavCardLoaded.current) {
			return;
		}
		hasFiredNavCardLoaded.current = true;

		const apiLoadedTime = performance.now();
		const totalDuration = apiLoadedTime - renderStartTime.current;
		fireAnalyticsEvent(
			onNonGenerativeAIAnswerResultShown({
				...analyticsAttributes,
				fmpLoadTime: totalDuration,
				resourceType: 'confluence',
			}),
		);
	}, [fireAnalyticsEvent, analyticsAttributes]);

	useEffect(() => {
		trackAIInteractionInit();
		fireSAINUsageEvent({});
	}, [fireSAINUsageEvent, trackAIInteractionInit]);

	const handleLinkClick = useCallback(
		({
			event,
			resourceUrl,
			location,
		}: {
			event: React.MouseEvent | React.KeyboardEvent;
			resourceUrl: string;
			location: string;
		}) => {
			event.preventDefault();
			fireAnalyticsEvent(
				onNonGenerativeKnowledgeCardLinkClick({ ...analyticsAttributes, location }),
			);
			fireSAINUsageEvent({});

			navigateToLink({ event, url: resourceUrl, onNavigate });
		},
		[fireAnalyticsEvent, analyticsAttributes, fireSAINUsageEvent, onNavigate],
	);

	const fireAnalyticsEventAndUsage = useCallback(
		(action: string) => {
			fireAnalyticsEvent(
				onNonGenerativeKnowledgeCardLinkClick({ ...analyticsAttributes, location: action }),
			);
			fireSAINUsageEvent({});
		},
		[fireAnalyticsEvent, analyticsAttributes, fireSAINUsageEvent],
	);

	const fireAnalyticsEventAndUsageForExpand = useCallback(
		(layout: 'single-column' | 'dual-column') => {
			fireAnalyticsEvent(
				onNonGenerativeKnowledgeCardLinkClick({
					...analyticsAttributes,
					location: `expand-${layout}`,
				}),
			);
			fireSAINUsageEvent({});
		},
		[fireAnalyticsEvent, analyticsAttributes, fireSAINUsageEvent],
	);

	if (twgSourcesLoading) {
		return <ConfluenceResultCardLoading />;
	}

	return (
		<SearchAIAnalyticsContext
			data={{
				attributes: {
					...analyticsAttributesWithoutUGC,
					location: SearchAIAnalyticsContextLocation.SEARCH_RESULT_CONFLUENCE_CARD,
				},
				location: SearchAIAnalyticsContextLocation.SEARCH_RESULT_CONFLUENCE_CARD,
			}}
		>
			<SmartCardProvider>
				<Stack xcss={styles.root}>
					<Box
						xcss={cx(styles.smartCardContainer, styles.borderBottomSeparator, styles.sidePadding)}
					>
						<ConfluenceFlexibleSmartCard
							url={url}
							cloudId={cloudId}
							contentId={contentId}
							contentType={contentType}
							description={description}
							areLLMPoweredFeaturesEnabled={areLLMPoweredFeaturesEnabled}
							workspaceId={workspaceId || ''}
							product={primaryProduct || ''}
							onLoad={fireNavCardLoaded}
							onClick={handleLinkClick}
							onActionButtonClicked={fireAnalyticsEventAndUsage}
							searchPageAnalyticsCallbacks={searchPageAnalyticsCallbacks}
						/>
					</Box>
					{(links.length > 0 || deduplicatedContributors.length > 0) && (
						<Box
							xcss={cx(
								styles.additionalInfoSectionContainer,
								styles.borderBottomSeparator,
								styles.sidePadding,
							)}
						>
							<AdditionalInfoSection
								links={links}
								contributors={deduplicatedContributors}
								cloudId={cloudId}
								onResourceClick={handleLinkClick}
								onContributorClick={fireAnalyticsEventAndUsage}
								onExpandClicked={fireAnalyticsEventAndUsageForExpand}
							/>
						</Box>
					)}
					<Box xcss={cx(styles.conversationStartersContainer, styles.sidePadding)}>
						<ConversationStartersBanner
							primaryProduct={primaryProduct}
							cloudId={cloudId}
							searchResult={{ ari: contentId, url: url }}
							analyticsAttributes={analyticsAttributesWithoutUGC}
						/>
					</Box>
				</Stack>
				<Box xcss={styles.feedbackContainer}>
					<AIDisclaimerAndFeedback
						showFeedbackButtons
						showAIUsageDisclaimer={areLLMPoweredFeaturesEnabled}
					/>
				</Box>
			</SmartCardProvider>
		</SearchAIAnalyticsContext>
	);
};

const ConfluenceCardComponentMemoized = memo(ConfluenceCardComponent);

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const ConfluenceResultCard = (props: NavigationalCardProps): React.JSX.Element => (
	<ConfluenceCardComponentMemoized {...props} />
);
